import { useEffect } from 'react'
import * as Sentry from '@sentry/react'
import { useNavigate } from '@tanstack/react-router'
import { useSession } from '@/lib/auth-client'
import { ROUTES } from '@/lib/constants'
import { Skeleton } from '@/components/ui/skeleton'

type AuthGuardProps = {
  children: React.ReactNode
}

export function AuthGuard({ children }: AuthGuardProps) {
  const navigate = useNavigate()
  const { data: session, isPending } = useSession()

  useEffect(() => {
    if (!isPending && !session) {
      navigate({ to: ROUTES.LOGIN })
    }
  }, [session, isPending, navigate])

  useEffect(() => {
    if (session?.user) {
      const { id, email, name } = session.user
      Sentry.setUser({ id, email, username: name })
    } else if (!isPending) {
      Sentry.setUser(null)
    }
  }, [session, isPending])

  if (isPending) {
    return <AuthLoadingSkeleton />
  }

  if (!session) {
    return null
  }

  return <>{children}</>
}

function AuthLoadingSkeleton() {
  return (
    <div className="min-h-screen flex">
      {/* Sidebar skeleton */}
      <div className="w-64 border-r p-4 space-y-4">
        <Skeleton className="h-8 w-32" />
        <div className="space-y-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-10 w-full" />
          ))}
        </div>
      </div>
      {/* Content skeleton */}
      <div className="flex-1 p-6 space-y-4">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-64 w-full" />
      </div>
    </div>
  )
}
